import { useEffect, useState } from "react";
import { Eye, FileUp, Globe, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import AuthImg, { openPdf } from "@/components/AuthImg";

const box = "bg-slate-900 border-2 border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-yellow-300 outline-none";
const inp = `w-full ${box}`;
const newQ = (n) => ({ id: Math.random().toString(36).slice(2, 12), n: String(n), text: "", typed: true, marks: 1, image: null, answers: [] });

function QuestionEditor({ q, pid, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...q, [k]: v });
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/test-papers/${pid}/questions/${q.id}/image`, fd);
      onChange({ ...q, image: data.items.find((x) => x.id === q.id)?.image || q.image });
      toast.success("Picture replaced");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save the question first, then add a picture"); }
  };
  return (
    <div className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-4 space-y-3" data-testid={`tp-q-${q.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-slate-400 text-sm">Question</span>
        <input value={q.n} onChange={(e) => set("n", e.target.value)} className={`${box} w-20`} data-testid={`tp-q-n-${q.id}`} />
        <span className="text-slate-400 text-sm">Marks</span>
        <input type="number" min="0" step="0.5" value={q.marks ?? ""} onChange={(e) => set("marks", e.target.value === "" ? null : Number(e.target.value))} className={`${box} w-20`} data-testid={`tp-q-marks-${q.id}`} />
        <label className="flex items-center gap-2 text-sm text-slate-200">
          <input type="checkbox" checked={q.typed} onChange={(e) => set("typed", e.target.checked)} data-testid={`tp-q-typed-${q.id}`} />
          Child types the answer {!q.typed && <span className="text-amber-300">(done on paper, not marked)</span>}
        </label>
        <button onClick={onRemove} className="ml-auto text-red-300 hover:text-red-200" title="Remove question" data-testid={`tp-q-remove-${q.id}`}><Trash2 size={16} /></button>
      </div>
      <textarea rows={3} value={q.text} onChange={(e) => set("text", e.target.value)} className={inp} data-testid={`tp-q-text-${q.id}`} />
      <div className="flex flex-wrap gap-4 items-start">
        <div className="flex-1 min-w-[220px]">
          <p className="text-xs text-slate-400 mb-1">Correct answer(s), one per line</p>
          <textarea rows={2} value={q.answers.join("\n")} onChange={(e) => set("answers", e.target.value.split("\n"))} disabled={!q.typed}
            className={`${inp} ${q.typed && !q.answers.some((a) => a.trim()) ? "border-red-400" : ""}`} data-testid={`tp-q-answers-${q.id}`} />
        </div>
        <div className="w-56">
          {q.image ? (
            <div className="bg-white rounded-lg p-1">
              <AuthImg src={`/test-papers/${pid}/image/${q.id}?v=${encodeURIComponent(q.image)}`} className="max-h-36 mx-auto" testId={`tp-q-img-${q.id}`} />
            </div>
          ) : <p className="text-xs text-slate-500">No picture</p>}
          <div className="flex gap-2 mt-2 text-xs">
            <label className="cursor-pointer text-yellow-300 inline-flex items-center gap-1" data-testid={`tp-q-img-upload-${q.id}`}>
              <FileUp size={13} /> {q.image ? "Replace" : "Add"} picture
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
            </label>
            {q.image && <button onClick={() => set("image", null)} className="text-slate-300 inline-flex items-center gap-1" data-testid={`tp-q-img-remove-${q.id}`}><X size={13} /> Remove</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TestPaperReview({ pid, onClose, onChanged }) {
  const [paper, setPaper] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = (d) => { setPaper(d); setItems(d.items.map((q) => ({ ...q, answers: q.answers || [] }))); };
  useEffect(() => { api.get(`/admin/test-papers/${pid}`).then(({ data }) => load(data)); }, [pid]);
  if (!paper) return <Loader2 className="animate-spin text-yellow-300 m-6" />;

  const save = async () => {
    const { data } = await api.put(`/admin/test-papers/${pid}/questions`, { questions: items });
    load(data);
    onChanged();
    return data;
  };
  const run = (fn, ok) => async () => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  const publish = (online) => run(async () => { await save(); const { data } = await api.post(`/admin/test-papers/${pid}/publish`, { online }); setPaper((p) => ({ ...p, online_status: data.online_status })); onChanged(); },
    online ? "Online for children" : "Taken offline");
  const live = paper.online_status === "published";

  return (
    <div className="mt-4 space-y-4" data-testid="tp-review">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-lg font-bold text-slate-100 mr-auto">Check the online version · {paper.title}</h3>
        <button onClick={() => openPdf(`/admin/test-papers/${pid}/file/paper`)} className="px-3 py-1.5 rounded-full border-2 border-slate-600 text-sm inline-flex items-center gap-1" data-testid="tp-review-open-paper"><Eye size={14} /> Paper</button>
        {paper.has_answers && <button onClick={() => openPdf(`/admin/test-papers/${pid}/file/answers`)} className="px-3 py-1.5 rounded-full border-2 border-slate-600 text-sm inline-flex items-center gap-1" data-testid="tp-review-open-answers"><Eye size={14} /> Answer key</button>}
        <button onClick={onClose} className="px-3 py-1.5 rounded-full border-2 border-slate-600 text-sm" data-testid="tp-review-close">Close</button>
      </div>
      <p className="text-sm text-slate-400">Each question appears on its own screen. Check the text, the picture and the correct answer(s). Marking ignores capitals, spaces and accents, and 0.5 = 1/2.</p>
      {items.map((q, i) => (
        <QuestionEditor key={q.id} q={q} pid={pid} onChange={(nq) => setItems((l) => l.map((x, j) => (j === i ? nq : x)))}
          onRemove={() => setItems((l) => l.filter((_, j) => j !== i))} />
      ))}
      <button onClick={() => setItems((l) => [...l, newQ(l.length + 1)])} className="w-full rounded-xl border-2 border-dashed border-slate-600 py-2.5 text-sm text-slate-300 inline-flex justify-center items-center gap-2" data-testid="tp-review-add"><Plus size={15} /> Add a question</button>
      <div className="flex flex-wrap gap-2 sticky bottom-3">
        <button disabled={busy} onClick={run(save, "Saved")} className="px-4 py-2 rounded-full bg-white text-slate-900 font-bold inline-flex items-center gap-2" data-testid="tp-review-save"><Save size={15} /> Save</button>
        <button disabled={busy} onClick={publish(!live)} className={`px-4 py-2 rounded-full font-bold inline-flex items-center gap-2 ${live ? "bg-slate-700 text-slate-100" : "bg-green-500 text-slate-900"}`} data-testid="tp-review-publish">
          <Globe size={15} /> {live ? "Take offline" : "Save & put online"}
        </button>
        {busy && <Loader2 className="animate-spin text-yellow-300 self-center" />}
      </div>
    </div>
  );
}
